import React, { useEffect, useMemo, useState } from "react";
import {
    UserPlus,
    Calendar,
    Search,
    Settings as Cog,
    X,
    Plus,
    Ticket,
    Code2,
    ShoppingBag,
    ShoppingCart,
    MessageSquare,
    Zap,
    PhoneCall,
    Sparkles,
    Loader2,
    Send,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

// "My actions" — currently-installed actions on this agent
const MY_ACTIONS = [
    {
        key: "collect_leads",
        title: "Collect Leads",
        type: "Lead capture",
        description: "Capture guest details (name, mobile, email, hotel) before the conversation begins.",
        icon: UserPlus,
        defaultConfig: {
            fields: ["hotel", "mobile", "name", "email"],
            required: true,
            successMessage: "Thank you! How can I help you today?",
        },
    },
    {
        key: "booking",
        title: "Booking",
        type: "Custom button",
        description: "Custom action that takes guests to a structured booking form for stays, dining and events.",
        icon: Calendar,
        defaultConfig: {
            buttonLabel: "Book a stay",
            redirectUrl: "https://www.orchidhotel.com/book",
            openInNewTab: true,
        },
    },
];

// "Create action" marketplace cards
const MARKETPLACE = [
    {
        key: "escalations",
        title: "Escalations",
        description: "Create a support ticket on your chosen ticketing system when a customer needs human help.",
        chips: ["Create ticket"],
        icon: Ticket,
        accent: "bg-rose-500/10 text-rose-600",
    },
    {
        key: "custom_actions",
        title: "Custom actions",
        description: "Create a custom action that calls your API, runs client-side code, and optionally displays an interactive widget.",
        chips: ["Call API", "Run client-side code", "Call API + show widget", "Show widget"],
        icon: Code2,
        accent: "bg-orchid-700/10 text-orchid-700",
    },
    {
        key: "collect_leads_template",
        title: "Collect leads",
        description: "Collect leads by capturing user details (name, email, phone, hotel) during the conversation.",
        chips: ["✓ Collect leads"],
        installedKey: "collect_leads",
        icon: UserPlus,
        accent: "bg-sky-500/10 text-sky-600",
    },
    {
        key: "fundle",
        title: "Fundle Loyalty",
        description: "Look up Orchid Rewards members, send OTP, redeem petals, manage membership tier upgrades.",
        chips: ["Lookup member", "Send OTP", "Redeem petals"],
        icon: Sparkles,
        accent: "bg-amber-500/10 text-amber-600",
    },
    {
        key: "stripe",
        title: "Stripe",
        description: "Handle billing, invoices, and subscription queries directly in chat.",
        chips: ["Invoices", "Subscriptions", "Change customer info", "Manage subscriptions"],
        icon: ShoppingCart,
        accent: "bg-indigo-500/10 text-indigo-600",
    },
    {
        key: "shopify",
        title: "Shopify",
        description: "Recommend products, answer order questions, and provide delivery updates.",
        chips: ["Products", "Get cart", "Orders", "Update profile"],
        icon: ShoppingBag,
        accent: "bg-emerald-500/10 text-emerald-600",
    },
    {
        key: "custom_form",
        title: "Custom form",
        description: "Create a custom form to collect information from your users.",
        chips: ["Form"],
        icon: Zap,
        accent: "bg-orange-500/10 text-orange-600",
    },
    {
        key: "suggested_messages",
        title: "Suggested messages",
        description: "Customize suggested messages based on the conversation.",
        chips: ["Suggested messages"],
        icon: MessageSquare,
        accent: "bg-yellow-500/10 text-yellow-700",
    },
    {
        key: "live_chat",
        title: "Live chat handoff",
        description: "Hand over from the AI agent to live chat with a human support agent.",
        chips: ["Live chat"],
        icon: PhoneCall,
        accent: "bg-violet-500/10 text-violet-600",
    },
];

export default function Actions() {
    const [tab, setTab] = useState("mine");
    const [actions, setActions] = useState({});
    const [customizations, setCustomizations] = useState({});
    const [saving, setSaving] = useState("");
    const [customizeKey, setCustomizeKey] = useState(null);
    const [customizeDraft, setCustomizeDraft] = useState({});
    const [savingCustomize, setSavingCustomize] = useState(false);
    const [search, setSearch] = useState("");
    const [installedToast, setInstalledToast] = useState({});

    const load = async () => {
        const [a, c] = await Promise.all([
            adminApi.get("/api/admin/actions"),
            adminApi.get("/api/admin/actions/customizations"),
        ]);
        setActions(a.data.actions || {});
        setCustomizations(c.data.customizations || {});
    };

    useEffect(() => {
        load();
    }, []);

    const toggle = async (key) => {
        setSaving(key);
        const next = { ...actions, [key]: !actions[key] };
        setActions(next);
        try {
            await adminApi.put("/api/admin/actions", { actions: next });
            toast.success(next[key] ? "Action enabled" : "Action disabled");
        } catch (err) {
            toast.error("Failed to save");
            setActions(actions);
        } finally {
            setSaving("");
        }
    };

    const openCustomize = (key) => {
        const def = MY_ACTIONS.find((a) => a.key === key)?.defaultConfig || {};
        setCustomizeKey(key);
        setCustomizeDraft({ ...def, ...(customizations[key] || {}) });
    };

    const saveCustomize = async () => {
        setSavingCustomize(true);
        try {
            await adminApi.put(`/api/admin/actions/${customizeKey}/customize`, {
                config: customizeDraft,
            });
            setCustomizations((prev) => ({ ...prev, [customizeKey]: customizeDraft }));
            toast.success("Customization saved");
            setCustomizeKey(null);
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setSavingCustomize(false);
        }
    };

    const installMarketplace = async (m) => {
        const installKey = m.installedKey || m.key;
        if (actions[installKey]) {
            toast.info("Already installed — switch to My actions to manage it.");
            setTab("mine");
            return;
        }
        // If we can install (matches a known action key), just flip it on
        if (MY_ACTIONS.find((a) => a.key === installKey)) {
            await toggle(installKey);
            setInstalledToast({ ...installedToast, [m.key]: true });
            setTimeout(() => setTab("mine"), 400);
            return;
        }
        // Otherwise log a beta request
        try {
            await adminApi.post("/api/admin/beta/request", {
                feature: `action_${m.key}`,
                notes: `Requested install for ${m.title}`,
            });
            toast.success(`We'll set up ${m.title} — interest recorded.`);
        } catch (err) {
            toast.error("Could not record request");
        }
    };

    const filteredMarketplace = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return MARKETPLACE;
        return MARKETPLACE.filter(
            (m) =>
                m.title.toLowerCase().includes(q) ||
                m.description.toLowerCase().includes(q) ||
                m.chips.some((c) => c.toLowerCase().includes(q))
        );
    }, [search]);

    return (
        <div className="p-10 max-w-6xl" data-testid="page-actions">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-2">
                    Workflow
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Actions</h1>
                <p className="text-stone-600 mt-2 max-w-2xl text-sm">
                    Configure what Priya can do during a conversation — collect leads, route bookings, and more.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-stone-200 mb-6">
                {[
                    { k: "mine", label: "My actions", icon: Zap },
                    { k: "create", label: "Create action", icon: Plus },
                ].map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`actions-tab-${t.k}`}
                        className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
                            tab === t.k
                                ? "border-stone-900 text-stone-900"
                                : "border-transparent text-stone-500 hover:text-stone-700"
                        }`}
                    >
                        <t.icon size={14} />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Search + filter */}
            <div className="flex items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-stone-400" size={14} />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search…"
                        data-testid="actions-search"
                        className="w-full pl-9 pr-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                    />
                </div>
                <select
                    className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white"
                    data-testid="actions-type-filter"
                >
                    <option>Type</option>
                </select>
            </div>

            {tab === "mine" ? (
                <div
                    className="bg-white border border-stone-200 rounded-xl overflow-hidden"
                    data-testid="actions-list"
                >
                    {MY_ACTIONS.filter((a) =>
                        !search ||
                        a.title.toLowerCase().includes(search.toLowerCase()) ||
                        a.description.toLowerCase().includes(search.toLowerCase())
                    ).map((a, i) => {
                        const enabled = !!actions[a.key];
                        return (
                            <div
                                key={a.key}
                                data-testid={`action-${a.key}`}
                                className={`p-5 flex items-center gap-4 ${
                                    i > 0 ? "border-t border-stone-100" : ""
                                }`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-orchid-700/10 text-orchid-700 flex items-center justify-center shrink-0">
                                    <a.icon size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-stone-900 text-sm">{a.title}</div>
                                    <div className="text-xs text-stone-500 mt-0.5">
                                        {a.type} · {a.description}
                                    </div>
                                </div>
                                <button
                                    onClick={() => toggle(a.key)}
                                    disabled={saving === a.key}
                                    data-testid={`toggle-${a.key}`}
                                    aria-pressed={enabled}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                        enabled ? "bg-orchid-700" : "bg-stone-300"
                                    } disabled:opacity-50`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                            enabled ? "translate-x-5" : "translate-x-0.5"
                                        }`}
                                    />
                                </button>
                                <button
                                    onClick={() => openCustomize(a.key)}
                                    data-testid={`customize-${a.key}`}
                                    className="flex items-center gap-1.5 text-xs text-stone-700 hover:text-orchid-700 px-3 py-1.5 rounded-md border border-stone-200 hover:border-orchid-700 transition-colors"
                                >
                                    <Cog size={13} /> Customize
                                </button>
                            </div>
                        );
                    })}
                    {MY_ACTIONS.length === 0 && (
                        <div className="p-12 text-center text-stone-500 text-sm">
                            No actions installed.{" "}
                            <button
                                className="text-orchid-700 underline"
                                onClick={() => setTab("create")}
                            >
                                Browse marketplace
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    data-testid="actions-marketplace"
                >
                    {filteredMarketplace.map((m) => {
                        const installKey = m.installedKey || m.key;
                        const isInstalled = !!actions[installKey];
                        return (
                            <button
                                key={m.key}
                                onClick={() => installMarketplace(m)}
                                data-testid={`marketplace-${m.key}`}
                                className="text-left bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-900 transition-colors flex flex-col h-full"
                            >
                                <div
                                    className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${m.accent}`}
                                >
                                    <m.icon size={20} />
                                </div>
                                <div className="font-medium text-stone-900 text-base mb-1">
                                    {m.title}
                                    {isInstalled && (
                                        <span className="ml-2 text-[10px] uppercase tracking-wider text-sage-700">
                                            Installed
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-stone-600 leading-relaxed mb-4 flex-1">
                                    {m.description}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {m.chips.map((c, i) => (
                                        <span
                                            key={i}
                                            className="text-[11px] px-2 py-1 rounded-md bg-stone-100 text-stone-700"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </button>
                        );
                    })}
                    {filteredMarketplace.length === 0 && (
                        <div className="col-span-full text-center text-sm text-stone-500 py-12">
                            No actions match &ldquo;{search}&rdquo;
                        </div>
                    )}
                </div>
            )}

            {/* Customize drawer */}
            {customizeKey && (
                <CustomizeDrawer
                    actionKey={customizeKey}
                    draft={customizeDraft}
                    setDraft={setCustomizeDraft}
                    onClose={() => setCustomizeKey(null)}
                    onSave={saveCustomize}
                    saving={savingCustomize}
                />
            )}
        </div>
    );
}

function CustomizeDrawer({ actionKey, draft, setDraft, onClose, onSave, saving }) {
    const meta = MY_ACTIONS.find((a) => a.key === actionKey);
    if (!meta) return null;
    const Icon = meta.icon;
    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            onClick={onClose}
            data-testid="customize-drawer"
        >
            <div
                className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-orchid-700/10 text-orchid-700 flex items-center justify-center">
                            <Icon size={16} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                Customize
                            </div>
                            <div className="font-heading text-lg text-orchid-700">{meta.title}</div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-stone-400 hover:text-stone-700"
                        data-testid="customize-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {actionKey === "collect_leads" && (
                        <>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                    Fields to capture (comma-separated)
                                </label>
                                <input
                                    value={(draft.fields || []).join(", ")}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            fields: e.target.value
                                                .split(",")
                                                .map((s) => s.trim())
                                                .filter(Boolean),
                                        })
                                    }
                                    data-testid="customize-collect-fields"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                                <p className="text-[11px] text-stone-500 mt-1">
                                    Default order: hotel, mobile, name, email.
                                </p>
                            </div>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!draft.required}
                                    onChange={(e) =>
                                        setDraft({ ...draft, required: e.target.checked })
                                    }
                                    className="accent-orchid-700"
                                />
                                Block conversation until all fields are filled
                            </label>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                    Success message
                                </label>
                                <textarea
                                    rows={3}
                                    value={draft.successMessage || ""}
                                    onChange={(e) =>
                                        setDraft({ ...draft, successMessage: e.target.value })
                                    }
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                        </>
                    )}

                    {actionKey === "booking" && (
                        <>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                    Button label
                                </label>
                                <input
                                    value={draft.buttonLabel || ""}
                                    onChange={(e) =>
                                        setDraft({ ...draft, buttonLabel: e.target.value })
                                    }
                                    data-testid="customize-booking-label"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                    Redirect URL
                                </label>
                                <input
                                    value={draft.redirectUrl || ""}
                                    onChange={(e) =>
                                        setDraft({ ...draft, redirectUrl: e.target.value })
                                    }
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!draft.openInNewTab}
                                    onChange={(e) =>
                                        setDraft({ ...draft, openInNewTab: e.target.checked })
                                    }
                                    className="accent-orchid-700"
                                />
                                Open in new tab
                            </label>
                        </>
                    )}

                    <div className="pt-3 border-t border-stone-200">
                        <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">
                            JSON preview
                        </div>
                        <pre className="text-[11px] bg-stone-50 border border-stone-200 rounded-md p-3 overflow-x-auto text-stone-700 font-mono">
                            {JSON.stringify(draft, null, 2)}
                        </pre>
                    </div>
                </div>

                <div className="px-5 py-4 border-t border-stone-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                    <button
                        onClick={onClose}
                        className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        disabled={saving}
                        data-testid="customize-save"
                        className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white disabled:opacity-50"
                    >
                        {saving ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Send size={13} />
                        )}
                        Save customization
                    </button>
                </div>
            </div>
        </div>
    );
}
